import axios from 'axios';
import {
  ActivityPayload,
  ActivitySubmission,
  AppliedProjectStat,
  DashboardPayload,
  LearningPlan,
  StaticLearningPlanUnit,
  LatestSubmissionPayload,
  toLegacyPreviewQuestion,
} from '../hydra';
import { AssessmentTestStep, AssessmentTestStepAnswer } from './admin';
import { apiURL } from './config';
import {
  getTeamsActivityRoute,
  getTeamsStatsRoute,
} from '@enki/contracts/common/product-reads';
import {
  getTeamsProjectRoute,
  getTeamsProjectsRoute,
  getTeamsProfileRoute,
  getAssessmentTestRoute,
  type GetTeamsProjectResponse,
} from '@enki/contracts/teams/reads';
import {
  submitTeamsProjectRoute,
  submitTeamsProjectAnswerRoute,
  submitCohortProjectRoute,
  createCompanyProjectUploadLinksRoute,
  type SubmitCohortProjectJsonBody,
} from '@enki/contracts/teams/writes';
import {
  syncAssessmentTestRoute,
  submitLLMAssessmentTestPromptRoute,
} from '@enki/contracts/teams/assessment-writes';
import {
  saveTeamsProfileRoute,
  postCohortOnboardingRoute,
  requestMentorReviewRoute,
  createTeamsProjectAIReviewRoute,
} from '@enki/contracts/teams/review-onboarding-writes';
import { requestContract } from './contract-client';

axios.defaults.baseURL = apiURL;
axios.defaults.headers.common['x-client-id'] = 'ENKI';

type TeamsModuleBase = {
  content: {
    title: string;
    slug: string;
    id: number;
    topic: string;
    imageUrl: string;
    description: string;
  };
  lessonsCount: number;
  coreLessonsCount: number;
  completedLessonsCount: number;
  coreCompletedLessonsCount: number;
  attemptedLessonsCount: number;
  coreAttemptedLessonsCount: number;
  finishedLessonsCount: number;
  finishedCoreLessonsCount: number;

  hasSubmission: boolean;

  firstIncompleteActivity: {
    missionSlug: string;
    activitySlug: string;
    activityName: string;
  } | null;

  firstUnattemptedActivity: {
    missionSlug: string;
    activitySlug: string;
    activityName: string;
  } | null;
};

export type JoinedTeamsModule = TeamsModuleBase & {
  isJoined: true;
  trackSlug: string;
  trackId: number;
};

type PlaceholderTeamsModule = TeamsModuleBase & {
  isJoined: false;
  trackSlug: string;
  trackId: number;
};

export type TeamsContentModule = JoinedTeamsModule | PlaceholderTeamsModule;

export type CompanyProjectSubmission = {
  type: 'CHECKIN_1' | 'CHECKIN_2' | 'PROJECT_UPLOAD';
  submitted_at: Date;
  file_links: Array<string> | null;
  external_links: Array<string> | null;
  survey: Array<{
    question: string;
    answer: string;
  }>;
};

export type CohortFeedbackSurvey = Record<
  string,
  { answer: number | string | Record<string, number>; question: string }
>;
export type CohortFeedback = CohortFeedbackSurvey;

export type NoProfileTeamsProfilePayload = {
  hasProfile: false;
  learnerId: null;
  company: null;
  onboardingAnswers: null;
  openCohortInvites: [];
  onboardedCohorts: [];
  learningPlan: null;
  modules: [];
  populatedLearningPlan: [];
  assessmentTests: [];
};

export type ProfileTeamsProfilePayload = {
  hasProfile: true;
  learnerId: number | null;
  company: {
    id: number;
    name: string;
    slug: string;
    settings: {
      KICK_OFF: boolean;
      WELCOME_EMAIL: boolean;
      RETRO_SURVEY: boolean;
      COMPANY_PROJECT_UPLOAD: boolean;
      OFFICE_HOURS_REMINDERS: boolean;
      LEARNING_PLAN_NOTIFICATIONS: boolean;
    };
  };
  onboardingAnswers: {
    'ask-name': string;
    'ask-role': string;
  } | null;
  openCohortInvites: Array<{
    id: number;
    cohort: {
      id: number;
      startDate: Date;
      endDate: Date;
      kickOffTimes: Array<string> | null;
      officeHourTimes: Array<string> | null;
    };
  }>;
  onboardedCohorts: Array<{
    cohort: {
      id: number;
      startDate: Date;
      endDate: Date;
    };
    onboardingAnswers: {
      [key: string]: string;
    } | null;
    milestones: {
      PROJECT_CHECKIN_1: {
        isLocked: boolean;
        isCompleted: boolean;
        submittedAt: Date | null;
        survey: CompanyProjectSubmission['survey'];
      } | null;
      PROJECT_CHECKIN_2: {
        isLocked: boolean;
        isCompleted: boolean;
        submittedAt: Date | null;
        survey: CompanyProjectSubmission['survey'];
      } | null;
      PROJECT_UPLOAD: {
        isLocked: boolean;
        isCompleted: boolean;
        submittedAt: Date | null;
        survey: CompanyProjectSubmission['survey'];
        file_links: Array<string> | null;
        external_links: Array<string> | null;
      } | null;
      COHORT_FEEDBACK: {
        isLocked: boolean;
        isCompleted: boolean;
        submittedAt: Date | null;
        survey: CohortFeedback;
      } | null;
    } | null;
  }>;
  populatedLearningPlan: StaticLearningPlanUnit[];
  learningPlan: LearningPlan | null;
  modules: Array<TeamsContentModule>;
  assessmentTests: Array<{
    sessionId: number;
    testSlug: string;
    startedAt: Date;
    finishedAt: Date | null;
    testTitle: string;
    companySlug: string;
  }>;
};

export type TeamsProfilePayload =
  | NoProfileTeamsProfilePayload
  | ProfileTeamsProfilePayload;

export async function getTeamsProfile(
  token: string
): Promise<TeamsProfilePayload> {
  const profile = await requestContract(getTeamsProfileRoute, { token });
  if (!isTeamsProfilePayload(profile)) {
    throw new Error('Invalid teams profile response');
  }
  return profile;
}

function isTeamsProfilePayload(value: unknown): value is TeamsProfilePayload {
  return (
    typeof value === 'object' &&
    value !== null &&
    'hasProfile' in value &&
    typeof value.hasProfile === 'boolean'
  );
}
export async function saveTeamsProfile(
  token: string,
  answers: {
    'ask-name': string;
    'ask-role': string;
    'ask-ideal-teacher': string[];
  },
  learnerId: number
) {
  return requestContract(saveTeamsProfileRoute, {
    token,
    body: { answers, learnerId },
  });
}

export async function postCohortOnboarding(
  token: string,
  answers: {
    'ask-notification-time': {
      timezone: string;
      time: string;
    };
    'ask-office-hours-time': string[];
    'ask-comments': string;
  },
  cohortInviteId: number
) {
  return requestContract(postCohortOnboardingRoute, {
    token,
    body: { answers, cohortInviteId },
  });
}

export async function requestMentorReview(
  token: string,
  {
    questionId,
    isRequestingReview,
    sessionId,
    type,
    comment,
  }: {
    questionId: number;
    isRequestingReview: boolean;
    sessionId: number;
    type: 'ACTIVITY' | 'PROJECT';
    comment: string;
  }
) {
  return requestContract(requestMentorReviewRoute, {
    token,
    body: {
      questionId,
      isRequestingReview,
      sessionId,
      type,
      comment,
    },
  });
}

export async function createCompanyProjectUploadLinks(
  token: string,
  {
    cohortId,
    learnerId,
    files,
  }: {
    cohortId: number;
    learnerId: number;
    files: Array<{ name: string; size: number; contentType: string }>;
  }
) {
  return requestContract(createCompanyProjectUploadLinksRoute, {
    token,
    params: { cohortId, learnerId },
    body: { files },
  });
}

/** Submits a company project whose files were already uploaded to S3. */
export async function submitCompanyProject(
  token: string,
  {
    cohortId,
    learnerId,
    body,
  }: {
    cohortId: number;
    learnerId: number;
    body: SubmitCohortProjectJsonBody;
  }
) {
  return requestContract(submitCohortProjectRoute, {
    token,
    params: { cohortId, learnerId },
    body,
  });
}

export async function postProjectSubmission(
  token: string,
  {
    formData,
    cohortId,
    learnerId,
  }: { formData: FormData; cohortId: string; learnerId: string }
): Promise<void> {
  await axios({
    method: 'post',
    url: `/teams/cohorts/${cohortId}/learners/${learnerId}/project`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: formData,
  });
  return;
}

export async function postCohortFeedback(
  token: string,
  {
    cohortId,
    learnerId,
    survey,
  }: {
    cohortId: string;
    learnerId: string;
    survey: CohortFeedbackSurvey;
  }
): Promise<void> {
  await axios({
    method: 'post',
    url: `/teams/cohorts/${cohortId}/learners/${learnerId}/feedback`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: { survey },
  });
  return;
}

export async function getTeamsProject(
  token: string,
  { projectSlug, companySlug }: { projectSlug: string; companySlug: string }
): Promise<ActivityPayload> {
  const project = await requestContract(getTeamsProjectRoute, {
    token,
    params: { companySlug, projectSlug },
  });
  return toLegacyTeamsProjectActivity(project);
}

export async function saveTeamsProject(
  token: string,
  {
    submission,
    companySlug,
    projectSlug,
  }: {
    submission: ActivitySubmission;
    companySlug: string;
    projectSlug: string;
  }
) {
  await requestContract(submitTeamsProjectRoute, {
    token,
    params: { companySlug, projectSlug },
    body: {
      ...submission,
      answer_states: Object.fromEntries(
        Object.entries(submission.answer_states).map(([slug, answerState]) => [
          slug,
          {
            ...answerState,
            submitted_at: answerState.submitted_at?.toISOString() ?? null,
          },
        ])
      ),
    },
  });
}

export async function getTeamsProjectsDashboard(
  token: string
): Promise<Array<AppliedProjectStat>> {
  const projects = await requestContract(getTeamsProjectsRoute, { token });
  return projects.map((project) => ({
    ...project,
    image_url: project.image_url ?? '',
    isLocked: false,
  }));
}

function toLegacyTeamsProjectActivity(
  project: GetTeamsProjectResponse
): ActivityPayload {
  return {
    id: project.activity_id,
    updated_at: project.exercise.updated_at,
    created_at: project.exercise.created_at,
    is_core: null,
    title: project.title,
    summary: project.summary,
    external_url: null,
    type: 'EXERCISE',
    mission_id: null,
    estimate_seconds: 0,
    index_in_mission: 0,
    exercise_id: project.exercise.id,
    is_teams_only: true,
    slug: project.slug,
    activity_session_id: project.activity_session_id,
    activity_session_instructor_review: null,
    activity_session_instructor_score: null,
    activity_session_instructor_question_comments: [],
    latest_submission: {
      id: project.latest_submission.id,
      updated_at: project.latest_submission.updated_at,
      created_at: project.latest_submission.created_at,
      difficulty_rating: project.latest_submission.difficulty_rating,
      feedback: project.latest_submission.feedback,
      seconds_spent: project.latest_submission.seconds_spent,
      activity_session_id: project.activity_session_id,
      ai_review: project.latest_submission
        .ai_review as NonNullable<LatestSubmissionPayload>['ai_review'],
      answer_states: project.latest_submission
        .answer_states as NonNullable<LatestSubmissionPayload>['answer_states'],
      mentor_reviews: project.latest_submission
        .mentor_reviews as NonNullable<LatestSubmissionPayload>['mentor_reviews'],
    },
    exercise: {
      id: project.exercise.id,
      updated_at: project.exercise.updated_at,
      created_at: project.exercise.created_at,
      description: project.exercise.description,
      lesson: project.exercise.lesson,
      difficulty: 0,
      activity_id: project.activity_id,
      slug: project.exercise.slug,
      questions: project.exercise.questions.map(toLegacyPreviewQuestion),
    },
    content_id: project.activity_id,
    content_slug: project.content_slug,
    content_title: project.content_title,
    next_activity_slug: null,
    next_mission_slug: null,
  };
}

export async function createTeamsProjectAIReview(
  token: string,
  {
    sessionId,
    projectSlug,
    companySlug,
  }: {
    sessionId: number;
    projectSlug: string;
    companySlug: string;
  }
) {
  await requestContract(createTeamsProjectAIReviewRoute, {
    token,
    params: { companySlug, projectSlug },
    body: { sessionId, projectSlug },
  });
}

export async function validateTeamsProjectAnswerAttempt(
  token: string,
  {
    value,
    submissionId,
    questionSlug,
    companySlug,
    projectSlug,
  }: {
    value: string;
    submissionId: number;
    questionSlug: string;
    companySlug: string;
    projectSlug: string;
  }
): Promise<void> {
  await requestContract(submitTeamsProjectAnswerRoute, {
    token,
    timeout: 1000 * 30,
    params: { companySlug, projectSlug },
    body: {
      value,
      submissionId,
      questionSlug,
    },
  });
}

export type AssessmentTestResponse = {
  assessmentTestId: number;
  assessmentTestSessionId: number;
  steps: Array<AssessmentTestStep>;
  answers: Array<AssessmentTestStepAnswer>;
  startedAt: Date | null;
  finishedAt: Date | null;
  companyId: number;
  finishReason: 'COMPLETED' | 'TIME_EXPIRED';
};

export async function getAssessmentTest(
  token: string,
  { companySlug, testSlug }: { companySlug: string; testSlug: string }
): Promise<AssessmentTestResponse> {
  const response = await requestContract(getAssessmentTestRoute, {
    token,
    params: { atCompanySlug: companySlug, atTestSlug: testSlug },
  });

  return {
    ...response,
    steps: response.steps as AssessmentTestStep[],
    answers: response.answers.map((answer) => ({
      ...answer,
      introduction: answer.introduction
        ? {
            ...answer.introduction,
            timeIntervals: answer.introduction.timeIntervals.map(
              (interval) => ({
                startedAt: new Date(interval.startedAt),
                endedAt: interval.endedAt ? new Date(interval.endedAt) : null,
              })
            ),
          }
        : null,
      sections: answer.sections.map((section) => ({
        ...section,
        timeIntervals: section.timeIntervals.map((interval) => ({
          startedAt: new Date(interval.startedAt),
          endedAt: interval.endedAt ? new Date(interval.endedAt) : null,
        })),
      })),
    })) as AssessmentTestStepAnswer[],
    startedAt: response.startedAt ? new Date(response.startedAt) : null,
    finishedAt: response.finishedAt ? new Date(response.finishedAt) : null,
  };
}

export async function syncAssessmentTestSession(
  token: string,
  {
    companyId,
    testId,
    testSessionId,
    data,
  }: {
    companyId: number;
    testId: number;
    testSessionId: number;
    data: {
      finishedAt: Date | null;
      startedAt: Date;
      finishReason: 'COMPLETED' | 'TIME_EXPIRED' | null;
      answers: AssessmentTestStepAnswer[];
    };
  }
) {
  return requestContract(syncAssessmentTestRoute, {
    token,
    params: { atCompanyId: companyId, atTestId: testId },
    body: {
      testSessionId,
      data: {
        ...data,
        startedAt: data.startedAt.toISOString(),
        finishedAt: data.finishedAt?.toISOString() ?? null,
        answers: data.answers.map((answer) => ({
          ...answer,
          introduction: answer.introduction
            ? {
                ...answer.introduction,
                timeIntervals: answer.introduction.timeIntervals.map(
                  (interval) => ({
                    startedAt: interval.startedAt.toISOString(),
                    endedAt: interval.endedAt?.toISOString() ?? null,
                  })
                ),
              }
            : null,
          sections: answer.sections.map((section) => ({
            ...section,
            timeIntervals: section.timeIntervals.map((interval) => ({
              startedAt: interval.startedAt.toISOString(),
              endedAt: interval.endedAt?.toISOString() ?? null,
            })),
          })),
        })),
      },
    },
  });
}

export async function submitLLMTestPrompt(
  token: string,
  {
    companyId,
    testId,
    stepId,
    sectionId,
    prompt,
    testSessionId,
  }: {
    companyId: number;
    testId: number;
    stepId: string;
    sectionId: string;
    prompt: string;
    testSessionId: number;
  }
) {
  return requestContract(submitLLMAssessmentTestPromptRoute, {
    token,
    params: { atCompanyId: companyId, atTestId: testId },
    body: { prompt, stepId, sectionId, testSessionId },
  });
}

export async function getTeamsActivity(
  token: string,
  {
    trackSlug,
    missionSlug,
    activitySlug,
  }: { trackSlug: string; missionSlug: string; activitySlug: string }
): Promise<ActivityPayload> {
  return requestContract(getTeamsActivityRoute, {
    token,
    params: { trackSlug, missionSlug, activitySlug },
  }) as Promise<ActivityPayload>;
}

export async function getTeamsDashboard(
  token: string,
  { trackSlug }: { trackSlug: string }
): Promise<DashboardPayload> {
  return requestContract(getTeamsStatsRoute, {
    token,
    params: { trackSlug },
  }) as Promise<DashboardPayload>;
}
